import { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { EventDrawer } from '@components/EventDrawer';
import { loadClientList, useClient, useFeaturesSettled } from '@lib/clients';

/**
 * EventPopout - the pop-out single-event window (William 2026-07-14): the drawer's content as its
 * own full-window page, so the operator can see one person at a time, in full, beside the queue.
 *
 * Opened by the drawer's "Pop out" button via same-origin window.open, which COPIES sessionStorage
 * into the child - the MSAL cache rides along and AuthRoute passes silently. A cold open (bookmark,
 * blocked copy) just lands on the normal login and returns here. Close = window.close(); Escape
 * (the drawer's existing handler) does the same. The route renders WITHOUT AppShell on purpose:
 * no nav, no queue chrome - the window itself is the one scrollbar.
 *
 * THE PLAN FIRST (16 Oct 2026). Only the shell read the plan list, and this page has no shell, so a
 * pop out knew only the two-plan floor: a BCBSM case was asked for under Alignment, answered 404, and
 * the window sat on "Loading event..." for good. The page now reads the plan list itself and opens the
 * case only once that read has answered, so the `?client=` the opener put on the URL (lib/open-mode.ts
 * eventPageUrl) is in force. The drawer is keyed on the plan, so a late change re-reads under it.
 */
export function EventPopout() {
  const { eventId } = useParams();
  const settled = useFeaturesSettled();
  const client = useClient();

  useEffect(() => {
    if (eventId) document.title = `Member Care · ${eventId}`;
  }, [eventId]);

  useEffect(() => {
    void loadClientList();
  }, []);

  if (!eventId) return <Navigate to="/" replace />;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper-2)' }}>
      {settled ? (
        <EventDrawer
          key={client}
          variant="page"
          eventId={eventId}
          onClose={() => window.close()}
          // The parent queue keeps its own freshness polling; nothing to follow from here.
          onChanged={() => {}}
        />
      ) : (
        <section className="drawer drawer-page" aria-label="Event detail">
          <div style={{ padding: 20 }} className="muted small">
            Loading event...
          </div>
        </section>
      )}
    </div>
  );
}
